import {createSlice} from "@reduxjs/toolkit";
import {userLogin, verifyMfaOtp} from "./authAction";
import {breadCrumbIdsAddHCP} from "commonUtils/enumerations";

export interface menu {
  id: number;
  displayText: string;
  displayOrder: number;
  pageToLoad: string;
  parentMenuItemId: number;
  displayMode: number;
  isNew: boolean;
  subMenus: subMenu[];
}

export interface subMenu {
  id: number;
  displayText: string;
  displayOrder: number;
  pageToLoad: string;
  parentMenuItemId: number;
  displayMode: number;
  isNew: boolean;
  pages: page[];
}

export interface page {
  id: number;
  displayText: string;
  displayOrder: number;
  pageToLoad: string;
  parentMenuItemId: number;
  displayMode: number;
  isNew: boolean;
}

export interface breadcrumb {
  id: number;
  breadCrumbName: string;
  displayText: string;
  displayOrder: number;
  menuItemId: number;
  pageToLoad: string;
  typeStatus: number;
}

export interface authDetails {
  isAuthenticated: boolean;
  token: string;
}

export interface userList {
  userId: number;
  userName: string;
}

export interface profileList {
  id: number;
  profileName: string;
  loginTypeId: number;
  systemKey: number;
  isPIAccess: boolean;
}

export interface header {
  isJumpToVisible: boolean;
  frJumpTo: string;
  cppJumpTo: string;
  userList: userList[];
  cpsUserProfiles: profileList[];
  showTrainingPopUp: boolean;
}

export interface securityQuestion1 {
  id: number;
  questions1: string;
}
export interface securityQuestion2 {
  id: number;
  questions2: string;
}
export interface facultyId {
  facultyId: number;
}

export interface userDetails {
  userIdentity: string;
  userId: number;
  profileId: number;
  userName: string;
  switchedUserId: number;
  switchedProfileId: number;
  switchedUserName: string;
  isChangePassword: boolean;
  isFirstTime: boolean;
  pageToLoad: string;
  message: string;
  isMultipleProfiles: boolean;
  llyReadOnlyUser: boolean;
  hasAccessToAlertPage: boolean;
  supplierId: number;
  switchedUserIdentity: string;
  userTypeId: number;
  roles: string;
  groupId: number;
  isSpeakersBureau: boolean;
  supplierShortName: string;
  email: string;
  isMfaApplicable: boolean;
  supplierName: string;
  profileName: string;
  securityQuestionSet1: securityQuestion1[];
  securityQuestionSet2: securityQuestion2[];
}

interface AuthState {
  authDetails: authDetails;
  userDetails: userDetails;
  userPreferences: {
    pageSize: number;
    pageCount: number;
    menus: menu[];
    breadcrumbs: breadcrumb[];
  };
  headerDetails: header;
  facultyDetails: facultyId;
}

const initialState: AuthState = {
  authDetails: {
    isAuthenticated: false,
    token: "",
  },
  userDetails: {
    userIdentity: "",
    userId: 0,
    profileId: 0,
    userName: "",
    switchedUserId: 0,
    switchedProfileId: 0,
    switchedUserName: "",
    isChangePassword: false,
    isMultipleProfiles: false,
    isFirstTime: false,
    pageToLoad: "",
    message: "",
    llyReadOnlyUser: false,
    hasAccessToAlertPage: false,
    supplierId: 0,
    switchedUserIdentity: "",
    userTypeId: 0,
    roles: "",
    groupId: 0,
    isSpeakersBureau: false,
    supplierShortName: "",
    email: "",
    isMfaApplicable: false,
    supplierName: "",
	profileName: "",
    securityQuestionSet1: [],
    securityQuestionSet2: [],
  },
  userPreferences: {
    pageSize: 0,
    pageCount: 0,
    menus: [],
    breadcrumbs: [],
  },
  headerDetails: {
    isJumpToVisible: false,
    frJumpTo: "",
    cppJumpTo: "",
    userList: [],
    cpsUserProfiles: [],
    showTrainingPopUp: false,
  },
  facultyDetails: {
    facultyId: 0,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthState: () => initialState,
    updateUserPreferences: (state, action) => {
      state.userPreferences = action.payload;
    },
    updateUserDetail: (state, action) => {
      state.userDetails.llyReadOnlyUser = action.payload.llyReadOnlyUser;
      state.userDetails.supplierId = action.payload.supplierId;
      state.userDetails.hasAccessToAlertPage =
        action.payload.hasAccessToAlertPage;
      state.userDetails.switchedUserIdentity =
        action.payload.switchedUserIdentity;
      state.userDetails.userTypeId = action.payload.userTypeId;
      state.userDetails.roles = action.payload.roles;
      state.userDetails.groupId = action.payload.groupId;
      state.userDetails.isSpeakersBureau = action.payload.isSpeakersBureau;
      state.userDetails.supplierShortName = action.payload.supplierShortName;
      state.userDetails.email = action.payload.email;
      state.userDetails.isMfaApplicable = action.payload.isMfaApplicable;
      state.userDetails.supplierName = action.payload.supplierName;
	  state.userDetails.profileName = action.payload.profileName;
    },
    updateUserSpecificDetail: (state, action) => {
      state.userDetails.llyReadOnlyUser = action.payload.llyReadOnlyUser;
      state.userDetails.supplierId = action.payload.supplierId;
      state.userDetails.userTypeId = action.payload.userTypeId;
    },
    updateAuthState: (state, action) => {
      state.authDetails.isAuthenticated = action.payload.isAuthenticated;
      state.authDetails.token = action.payload.token;
    },
    updateSwitchedUserDetails: (state, action) => {
      state.userDetails.userIdentity = action.payload.userIdentity;
      state.userDetails.userId = action.payload.userId;
      state.userDetails.profileId = action.payload.profileId;
      state.userDetails.userName = action.payload.userName;
      state.userDetails.switchedUserId = action.payload.switchedUserId;
      state.userDetails.switchedProfileId = action.payload.switchedProfileId;
      state.userDetails.switchedUserName = action.payload.switchedUserName;
    },
    updateHeaderDetails: (state, action) => {
      state.headerDetails = action.payload;
    },
    updateFacultyBreadCrumbs: (state, action) => {
      state.userPreferences.breadcrumbs.forEach(item => {
        if (item.id === action.payload.id) {
          item.typeStatus = action.payload.status;
        }
      });
    },
    clearBreadCrumbState: state => {
      const idsToUpdate = [
        breadCrumbIdsAddHCP.PROFILE,
        breadCrumbIdsAddHCP.CONTACTINFO,
        breadCrumbIdsAddHCP.SPONSORINFO,
      ];
      state.userPreferences.breadcrumbs.forEach(item => {
        if (idsToUpdate.includes(item.id)) {
          item.typeStatus = 0;
        }
      });
    },
    updateFacultyId: (state, action) => {
      state.facultyDetails.facultyId = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(userLogin.fulfilled, mapFulFilled);
    builder.addCase(userLogin.rejected, mapRejected);
    builder.addCase(verifyMfaOtp.fulfilled, mapFulFilled);
    builder.addCase(verifyMfaOtp.rejected, mapRejected);
  },
});

export const {
  clearAuthState,
  updateUserPreferences,
  updateUserDetail,
  updateUserSpecificDetail,
  updateAuthState,
  updateSwitchedUserDetails,
  updateHeaderDetails,
  updateFacultyBreadCrumbs,
  clearBreadCrumbState,
  updateFacultyId,
} = authSlice.actions;
export default authSlice.reducer;

const mapRejected = (state: any, action: any) => {
  clearAuthState();
  state.userDetails.message = (action.payload as userDetails).message;
};

const mapFulFilled = (state: any, action: any) => {
  state.authDetails.isAuthenticated = action.payload.isAuthenticated;
  state.authDetails.token = action.payload.token;
  state.userDetails.userIdentity = action.payload.userIdentity;
  state.userDetails.userId = action.payload.userId;
  state.userDetails.profileId = action.payload.profileId;
  state.userDetails.userName = action.payload.userName;
  state.userDetails.isChangePassword = action.payload.isChangePassword;
  state.userDetails.isMultipleProfiles = action.payload.isMultipleProfiles;
  state.userDetails.isFirstTime = action.payload.isFirstTime;
  state.userDetails.pageToLoad = action.payload.pageToLoad;
  state.userDetails.message = action.payload.message;
  state.userDetails.isMfaApplicable = action.payload.isMfaApplicable;
  state.userDetails.securityQuestionSet1 = action.payload.securityQuestionSet1;
  state.userDetails.securityQuestionSet2 = action.payload.securityQuestionSet2;
  state.userPreferences = action.payload.userPreferences;
  if (Object.keys(action.payload.userProfiles || {}).length > 0) {
    state.headerDetails.cpsUserProfiles = action.payload.userProfiles.map(
      (x: {id: number; name: string}): profileList => {
        return {
          id: x.id,
          profileName: x.name,
          loginTypeId: 1,
          systemKey: 1,
          isPIAccess: false,
        };
      },
    );
  }
};
